/**
 * Stock Tab for Raw Product Drawer
 * Displays current stock level with history
 * @see US-11.9 - Widget Stock dans Drawer Produit
 */

import { useState } from 'react';
import {
  Package,
  ArrowDownCircle,
  ArrowUpCircle,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import { formatCentimes, formatQuantity } from '@/lib/format';
import {
  useProductStock,
  useProductStockHistory,
} from '@/features/stock-movements';
import { MovementType, MOVEMENT_REASON_LABELS } from '@bstock/shared';
import type { ProductStock, StockMovementListItem } from '@bstock/shared';
import { useFormatDate } from '@/hooks';

interface StockTabProps {
  productId: string;
  className?: string;
  showFinancials?: boolean;
}

export function StockTab({ productId, className, showFinancials = false }: StockTabProps) {
  const [expanded, setExpanded] = useState(false);
  const { data: stock, isLoading } = useProductStock(productId);
  const { data: history } = useProductStockHistory(productId, 1, 5);
  const { formatDateShort, formatDateTime } = useFormatDate();

  if (isLoading) {
    return (
      <div
        className={cn(
          'bg-white rounded-lg border border-neutral-200 p-4',
          className
        )}
      >
        <div className="animate-pulse">
          <div className="h-4 bg-neutral-200 rounded w-24 mb-2" />
          <div className="h-8 bg-neutral-200 rounded w-32" />
        </div>
      </div>
    );
  }

  if (!stock) {
    return (
      <div
        className={cn(
          'bg-white rounded-lg border border-neutral-200 p-4',
          className
        )}
      >
        <div className="flex items-center gap-2 text-neutral-500">
          <Package className="w-5 h-5" />
          <span className="text-sm">Aucun mouvement de stock</span>
        </div>
      </div>
    );
  }

  const getStockStatusBadge = (status: ProductStock['stockStatus']) => {
    switch (status) {
      case 'critical':
        return <Badge variant="danger">Critique</Badge>;
      case 'low':
        return <Badge variant="warning">Bas</Badge>;
      case 'overstocked':
        return <Badge variant="info">Surstock</Badge>;
      default:
        return <Badge variant="success">OK</Badge>;
    }
  };

  const getStockStatusIcon = (status: ProductStock['stockStatus']) => {
    switch (status) {
      case 'critical':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'low':
        return <TrendingDown className="w-5 h-5 text-amber-500" />;
      case 'overstocked':
        return <TrendingUp className="w-5 h-5 text-blue-500" />;
      default:
        return <Package className="w-5 h-5 text-green-500" />;
    }
  };

  return (
    <div
      className={cn(
        'bg-white rounded-lg border border-neutral-200 overflow-hidden',
        className
      )}
    >
      {/* Header - Current Stock */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {getStockStatusIcon(stock.stockStatus)}
          <div className="text-left">
            <div className="text-sm font-medium text-neutral-700">
              Stock actuel
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-neutral-900">
                {formatQuantity(stock.currentStock)}
              </span>
              <span className="text-sm text-neutral-500">
                {stock.rawProduct.recipeUnit.symbol}
              </span>
              {getStockStatusBadge(stock.stockStatus)}
            </div>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-neutral-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-neutral-400" />
        )}
      </button>

      {/* Expanded: Stock details and history */}
      {expanded && (
        <div className="border-t border-neutral-200">
          {/* Stock Info */}
          <div className="p-4 bg-neutral-50 grid grid-cols-2 gap-4">
            {showFinancials && (
              <>
                <div>
                  <div className="text-xs text-neutral-500 uppercase tracking-wider">
                    Valorisation
                  </div>
                  <div className="text-lg font-semibold text-neutral-800 mt-1">
                    {formatCentimes(stock.stockValue)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-neutral-500 uppercase tracking-wider">
                    PMP
                  </div>
                  <div className="text-lg font-semibold text-neutral-800 mt-1">
                    {stock.currentPmp ? formatCentimes(stock.currentPmp) : '-'}
                    <span className="text-xs text-neutral-500 ml-1">
                      / {stock.rawProduct.recipeUnit.symbol}
                    </span>
                  </div>
                </div>
              </>
            )}
            {stock.rawProduct.minStock !== null && (
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-wider">
                  Stock min
                </div>
                <div className="text-sm font-medium text-neutral-700 mt-1">
                  {stock.rawProduct.minStock} {stock.rawProduct.recipeUnit.symbol}
                </div>
              </div>
            )}
            {stock.rawProduct.maxStock !== null && (
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-wider">
                  Stock max
                </div>
                <div className="text-sm font-medium text-neutral-700 mt-1">
                  {stock.rawProduct.maxStock} {stock.rawProduct.recipeUnit.symbol}
                </div>
              </div>
            )}
          </div>

          {/* Recent Movements */}
          {history && history.data.length > 0 && (
            <div className="p-4">
              <h4 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
                Derniers mouvements
              </h4>
              <div className="space-y-2">
                {history.data.map((movement: StockMovementListItem) => (
                  <div
                    key={movement.id}
                    className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      {movement.type === MovementType.IN ? (
                        <ArrowDownCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <ArrowUpCircle className="w-4 h-4 text-red-500" />
                      )}
                      <div>
                        <div className="text-sm text-neutral-700">
                          {MOVEMENT_REASON_LABELS[movement.reason]}
                        </div>
                        <div className="text-xs text-neutral-400">
                          {formatDateShort(movement.eventDate)}
                        </div>
                      </div>
                    </div>
                    <span
                      className={cn(
                        'font-medium',
                        movement.type === MovementType.IN
                          ? 'text-green-600'
                          : 'text-red-600'
                      )}
                    >
                      {movement.type === MovementType.IN ? '+' : ''}
                      {formatQuantity(movement.signedQuantity)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Last update */}
          {stock.lastMovementDate && (
            <div className="px-4 pb-4 text-xs text-neutral-400">
              Dernier mouvement:{' '}
              {formatDateTime(stock.lastMovementDate)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
